import React, { useCallback, useState } from "react";

import Col from "antd/es/col";
import Row from "antd/es/row";
import Select from "antd/es/select";
import { FormattedMessage, useIntl } from "react-intl";

import { IntegrationMetadataInstance } from "@mapmycustomers/shared/types/integrations/IntegrationMetadataInstance";
import { Button, Modal } from "@mapmycustomers/ui";

import layout from "@app/styles/layout";

import useCancelIntegrationModalConfirm from "../../utils/useCancelIntegrationModalConfirm";

import styles from "./SelectDynamicsInstanceModal.module.scss";

interface Props {
  instances: IntegrationMetadataInstance[];
  onHide: (instanceUrl?: IntegrationMetadataInstance["instanceUrl"]) => void;
}

const SelectDynamicsInstanceModal: React.FC<Props> = ({ instances, onHide }) => {
  const intl = useIntl();
  const [instanceUrl, setInstanceUrl] = useState<
    IntegrationMetadataInstance["instanceUrl"] | undefined
  >();
  const [modalConfirm] = useCancelIntegrationModalConfirm(onHide);

  const handleSelect = useCallback(() => {
    onHide(instanceUrl);
  }, [onHide, instanceUrl]);

  return (
    <Modal
      closable={false}
      footer={[
        <Row gutter={layout.spacerM} justify="end" key="footer">
          <Col>
            <Button onClick={modalConfirm}>
              <FormattedMessage
                id="integrations.selectDynamicInstanceModal.footer.whatAreCustomFields"
                defaultMessage="Cancel"
                description="Text of select dynamic instance cancel button"
              />
            </Button>
          </Col>
          <Col>
            <Button disabled={!instanceUrl} key="submit" onClick={handleSelect} type="primary">
              <FormattedMessage
                id="integrations.selectDynamicInstanceModal.footer.gotIt"
                defaultMessage="Continue"
                description="Text of select dynamic instance set button"
              />
            </Button>
          </Col>
        </Row>,
      ]}
      title={intl.formatMessage({
        id: "integrations.selectDynamicInstanceModal.title",
        defaultMessage: "Select Microsoft Dynamics Instance",
        description: "Title select dynamic instance modal",
      })}
      visible
    >
      <div>
        <p className={styles.paragraph}>
          <FormattedMessage
            id="integrations.selectDynamicInstanceModal.body.paragraph"
            defaultMessage="You may only select one instance to link with Map My Customers."
            description="Mmc Select dynamic instance paragraph"
          />
        </p>
        <Select<IntegrationMetadataInstance["instanceUrl"]>
          className={styles.select}
          onChange={setInstanceUrl}
          placeholder={intl.formatMessage({
            id: "integrations.selectDynamicInstanceModal.placeholder",
            defaultMessage: "Select",
            description: "Title select dynamic instance modal - placeholder",
          })}
        >
          {instances.map((instance) => (
            <Select.Option key={instance.instanceUrl} value={instance.instanceUrl}>
              {instance.friendlyName}
            </Select.Option>
          ))}
        </Select>
      </div>
    </Modal>
  );
};

export default SelectDynamicsInstanceModal;
